import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { Link, useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  ArrowLeft,
  Save,
  X,
  Plus,
  Trash,
  Image,
  Film,
  Edit,
  Eye,
} from "lucide-react";

import {
  createProfile,
  getProfile,
  updateProfile,
  getPackagesMemberships,
  clearProfile,
  clearNewProfileCreated,
  uploadProfileBannerAsset,
  deleteProfileBannerAsset,
} from "../../actions/profile";

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const initialState = {
  name: "",
  cansLimit: 30,
  profileType: "default",
  region: [],
  welcome: { name: "Welcome", bundles: [] },
  popular: { name: "Popular", bundles: [] },
  current: { name: "Current", bundles: [] },
  accessories: { name: "Accessories", bundles: [] },
  refundableDeposit: { name: "Refundable Deposit", bundles: [] },
  banner: [],
  welcomeBanner: {
    asset: "",
    assetType: "image",
    active: false,
    closeIcon: {
      color: ""
    },
    button: {
      buttonText: "",
      textColor: "",
      backgroundColor: ""
    }
  },
  membership: "",
};

const bannerItemInitialState = {
  priority: 1,
  assetType: "image",
  asset: "",
};

const Profile = ({
  createProfile,
  getProfile,
  updateProfile,
  getPackagesMemberships,
  clearProfile,
  clearNewProfileCreated,
  uploadProfileBannerAsset,
  deleteProfileBannerAsset,
  profile,
  loading,
  packages,
  memberships,
  newProfileCreated,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialState);
  const [selectedCategory, setSelectedCategory] = useState("welcome");
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [newBannerItem, setNewBannerItem] = useState(bannerItemInitialState);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showWelcomeBannerForm, setShowWelcomeBannerForm] = useState(false);
  const [welcomeBannerFile, setWelcomeBannerFile] = useState(null);
  const [uploadingWelcomeBanner, setUploadingWelcomeBanner] = useState(false);
  const [editingCategoryName, setEditingCategoryName] = useState(null);
  const [tempCategoryName, setTempCategoryName] = useState("");

  const isNewProfile = id === "new";

  useEffect(() => {
	document.title = isNewProfile ? 'Create Profile' : 'Edit Profile';
	// Always fetch packages and memberships on component mount
	getPackagesMemberships();
  }, []);

  // Handle profile loading and form data setup
  useEffect(() => {
	if (isNewProfile && !newProfileCreated) {
	  clearProfile();
	  setFormData(initialState);
	} else if (isNewProfile && newProfileCreated) {
	  // Handle successful profile creation redirect
	  clearNewProfileCreated();
	  navigate(`/inventory/v1.2/profiles/${profile.productProfile._id}`);
	} else if (!profile.productProfile || profile.productProfile._id !== id) {
	  getProfile(id);
	} else if (profile.productProfile._id === id) {
	  // Ensure welcomeBanner has proper structure
	  const profileData = { ...initialState, ...profile.productProfile };
	  if (profileData.welcomeBanner) {
		profileData.welcomeBanner = {
		  ...initialState.welcomeBanner,
		  ...profileData.welcomeBanner,
		  closeIcon: {
			...initialState.welcomeBanner.closeIcon,
			...profileData.welcomeBanner.closeIcon
		  },
		  button: {
			...initialState.welcomeBanner.button,
			...profileData.welcomeBanner.button
		  }
		};
	  }
	  setFormData(profileData);
	}
  }, [id, isNewProfile, profile.productProfile, newProfileCreated]);

  const categories = [
	{ key: "welcome", name: formData.welcome?.name || "Welcome", color: "primary" },
	{ key: "popular", name: formData.popular?.name || "Popular", color: "success" },
	{ key: "current", name: formData.current?.name || "Current", color: "info" },
	{ key: "accessories", name: formData.accessories?.name || "Accessories", color: "warning" },
	{
	  key: "refundableDeposit",
	  name: formData.refundableDeposit?.name || "Refundable Deposit",
	  color: "secondary",
	},
  ];

  const handleChange = (e) => {
	const { name, value, type, checked } = e.target;
	setFormData({
	  ...formData,
	  [name]: type === "checkbox" ? checked : value,
	});
  };

  const handleCategoryChange = (category, bundles) => {
	setFormData({
	  ...formData,
	  [category]: {
		...formData[category],
		bundles: bundles,
	  },
	});
  };

  const handleCategoryNameChange = (category, newName) => {
	setFormData({
	  ...formData,
	  [category]: {
		...formData[category],
		name: newName,
	  },
	});
  };

  const startEditingCategoryName = (category) => {
	setEditingCategoryName(category);
	setTempCategoryName(formData[category]?.name || "");
  };

  const saveCategoryName = (category) => {
	if (tempCategoryName.trim()) {
	  handleCategoryNameChange(category, tempCategoryName.trim());
	}
	setEditingCategoryName(null);
	setTempCategoryName("");
  };

  const cancelEditingCategoryName = () => {
	setEditingCategoryName(null);
	setTempCategoryName("");
  };

  const addPackageToCategory = (category, packageId) => {
	if (!packageId) return;
	const newBundle = {
	  productPackage: packageId,
	  priority: formData[category].bundles.length + 1,
	};

	const updatedBundles = [...formData[category].bundles, newBundle];
	handleCategoryChange(category, updatedBundles);
  };

  const removePackageFromCategory = (category, index) => {
	const updatedBundles = formData[category].bundles.filter(
	  (_, i) => i !== index,
	);
	handleCategoryChange(category, updatedBundles);
  };

  const updatePackagePriority = (category, index, priority) => {
	const updatedBundles = [...formData[category].bundles];
	updatedBundles[index].priority = parseInt(priority);
	handleCategoryChange(category, updatedBundles);
  };

  const addBannerItem = async () => {
	if (!newBannerItem.asset.trim()) return;
	
	let finalAssetPath = newBannerItem.asset;
	// If there's a selected file, upload it first
	if (selectedFile) {
	  setUploadingFile(true);
	  try {
		finalAssetPath = await uploadFileToServer(selectedFile);
	  } catch (error) {
		alert("Error uploading file: " + error.message);
		setUploadingFile(false);
		return;
	  }
	  setUploadingFile(false);
	}
	
	const updatedBanners = [
	  ...formData.banner,
	  {
		...newBannerItem,
		asset: finalAssetPath,
		priority: formData.banner.length + 1,
	  },
	];
	setFormData({ ...formData, banner: updatedBanners });
	setNewBannerItem(bannerItemInitialState);
	setSelectedFile(null);
	setShowBannerForm(false);
  };

  const removeBannerItem = async (index) => {
	const bannerToRemove = formData.banner[index];
	// If the asset was uploaded to server (not a manual path/URL), delete it from server
	if (
	  bannerToRemove &&
	  bannerToRemove.asset &&
	  !bannerToRemove.asset.startsWith("http")
	) {
	  try {
		const productProfileId = isNewProfile ? "newProfile" : profile.productProfile._id;
		await deleteProfileBannerAsset(productProfileId, "banner", bannerToRemove.asset);
	  } catch (error) {
		console.error("Error deleting banner asset from server:", error);
	  }
	}
	const updatedBanners = formData.banner.filter((_, i) => i !== index);
	setFormData({ ...formData, banner: updatedBanners });
  };

  const openBannerForm = () => {
	setNewBannerItem(bannerItemInitialState);
	setPreviewAsset(null);
	setSelectedFile(null);
	setShowBannerForm(true);
  };

  // Welcome Banner functions
  const handleWelcomeBannerChange = (field, value) => {
	if (field.includes('.')) {
	  const [parent, child] = field.split('.');
	  setFormData({
		...formData,
		welcomeBanner: {
		  ...formData.welcomeBanner,
		  [parent]: {
			...formData.welcomeBanner?.[parent],
			[child]: value
		  }
		}
	  });
	} else {
	  setFormData({
		...formData,
		welcomeBanner: {
		  ...formData.welcomeBanner,
		  [field]: value
		}
	  });
	}
  };

  const uploadWelcomeBannerAsset = async () => {
	if (!welcomeBannerFile) return;
	
	setUploadingWelcomeBanner(true);
	try {
	  const finalAssetPath = await uploadFileToServer(welcomeBannerFile);
	  setFormData({
		...formData,
		welcomeBanner: {
		  ...formData.welcomeBanner,
		  asset: finalAssetPath,
		  assetType: welcomeBannerFile.type.startsWith("video/") ? "video" : "image"
		}
	  });
	  setWelcomeBannerFile(null);
	} catch (error) {
	  alert("Error uploading welcome banner: " + error.message);
	} finally {
	  setUploadingWelcomeBanner(false);
	}
  };

  const handleFileSelect = (e) => {
	const file = e.target.files[0];
	if (file) {
	  setSelectedFile(file);
	  setNewBannerItem({
		...newBannerItem,
		asset: file.name, // Show filename in the input
		assetType: file.type.startsWith("video/") ? "video" : "image",
	  });
	}
  };

  const handleWelcomeBannerFileSelect = (e) => {
	const file = e.target.files[0];
	if (file) {
	  setWelcomeBannerFile(file);
	}
  };

  const uploadFileToServer = async (file) => {
	try {
	  // Create FormData for file upload
	  const uploadFormData = new FormData();
	  uploadFormData.append("file", file);
	  
	  // Use the Redux action to upload
	  const result = await uploadProfileBannerAsset(uploadFormData);
	  
	  if (result) {
		// Use the returned filename from the server, or just the filename
		const uploadedPath =
		  result.filename || result.assetPath || result.path || file.name;
		return uploadedPath;
	  } else {
		throw new Error("Upload failed");
	  }
	} catch (error) {
	  console.error("File upload error:", error);
	  throw error;
	}
  };


  const onSubmit = (e) => {
	e.preventDefault();
	if (isNewProfile) {
	  createProfile(formData);
	} else {
	  updateProfile(profile.productProfile._id, formData);
	}
  };

  const getPackageName = (packageId) => {
	const pkg = packages?.find((p) => p._id === packageId);
	return pkg ? pkg.name : "Unknown Package";
  };

  const getMembershipName = (membershipId) => {
	const membership = memberships?.find((m) => m._id === membershipId);
	return membership ? membership.name : "Select Membership";
  };

  return (
	<Fragment>
	  <div className="container-fluid py-4">
		{/* Header */}
		<div className="row mb-4">
		  <div className="col-12">
			<div className="d-flex justify-content-between align-items-center">
			  <Link
				className="btn btn-outline-primary d-flex align-items-center"
				to="/inventory/v1.2/profiles"
			  >
				<ArrowLeft size={16} className="me-2" />
				Back to Profiles
			  </Link>
			  <h2 className="text-primary mb-0 fw-bold">
				{isNewProfile
				  ? "Create New Profile"
				  : `Edit Profile - ${formData.name}`}
			  </h2>
			  <div></div>
			</div>
		  </div>
		</div>

		<form onSubmit={onSubmit}>
		  <div className="row">
			{/* Main Content */}
			<div className="col-lg-8">
			  {/* Basic Information */}
			  <div className="card shadow-sm mb-4">
				<div className="card-header bg-primary text-white">
				  <h5 className="mb-0 d-flex align-items-center">
					<i className="fas fa-user me-2"></i>
					Basic Information
				  </h5>
				</div>
				<div className="card-body">
				  <div className="row">
					<div className="col-md-6 mb-3">
					  <label className="form-label fw-bold">Profile Name</label>
					  <input
						type="text"
						name="name"
						value={formData.name}
						onChange={handleChange}
						className="form-control form-control-lg"
						placeholder="Enter profile name"
						required
					  />
					</div>
					<div className="col-md-6 mb-3">
					  <label className="form-label fw-bold">Profile Type</label>
					  <select
						name="profileType"
						value={formData.profileType}
						onChange={handleChange}
						className="form-select form-select-lg"
					  >
						<option value="default">Default</option>
						<option value="live">Live</option>
					  </select>
					</div>
					<div className="col-md-6 mb-3">
					  <label className="form-label fw-bold">Cans Limit</label>
					  <input
						type="number"
						name="cansLimit"
						value={formData.cansLimit}
						onChange={handleChange}
						className="form-control"
						placeholder="30"
						min="1"
					  />
					</div>
					<div className="col-md-6 mb-3">
					  <label className="form-label fw-bold">Membership</label>
					  <select
						name="membership"
						value={formData.membership}
						onChange={handleChange}
						className="form-select"
					  >
						<option value="">Select Membership</option>
						{memberships?.map((membership) => (
						  <option key={membership._id} value={membership._id}>
							{membership.name}
						  </option>
						))}
					  </select>
					</div>
				  </div>
				</div>
			  </div>

			  {/* Product Categories */}
			  <div className="card shadow-sm mb-4">
				<div className="card-header bg-success text-white">
				  <h5 className="mb-0 d-flex align-items-center">
					<i className="fas fa-boxes me-2"></i>
					Product Categories
				  </h5>
				</div>
				<div className="card-body">
				  {/* Category Tabs */}
				  <ul className="nav nav-pills mb-3" role="tablist">
					{categories.map((category) => (
					  <li key={category.key} className="nav-item me-2 mb-2">
						<div className="d-flex align-items-center">
						  {editingCategoryName === category.key ? (
							<div className="d-flex align-items-center">
							  <input
								type="text"
								value={tempCategoryName}
								onChange={(e) => setTempCategoryName(e.target.value)}
								className="form-control form-control-sm me-2"
								style={{ width: "150px" }}
								placeholder="Category name"
								autoFocus
								onKeyDown={(e) => {
								  if (e.key === "Enter") {
									saveCategoryName(category.key);
								  } else if (e.key === "Escape") {
									cancelEditingCategoryName();
								  }
								}}
							  />
							  <button
								type="button"
								className="btn btn-success btn-sm me-1"
								onClick={() => saveCategoryName(category.key)}
								title="Save"
							  >
								<Save size={12} />
							  </button>
							  <button
								type="button"
								className="btn btn-outline-danger btn-sm"
								onClick={cancelEditingCategoryName}
								title="Cancel"
							  >
								<X size={12} />
							  </button>
							</div>
						  ) : (
							<>
							  <button
								type="button"
								className={`nav-link ${selectedCategory === category.key ? "active" : ""} btn-outline-${category.color}`}
								onClick={() => setSelectedCategory(category.key)}
							  >
								{category.name}
								<span className={`badge bg-${category.color} ms-2`}>
								  {formData[category.key]?.bundles?.length || 0}
								</span>
							  </button>
							  <button
								type="button"
								className="btn btn-outline-secondary btn-sm ms-2"
								onClick={() => startEditingCategoryName(category.key)}
								title="Edit category name"
							  >
								<Edit size={12} />
							  </button>
							</>
						  )}
						</div>
					  </li>
					))}
				  </ul>

				  {/* Category Content */}
				  <div className="tab-content">
					{categories.map(
					  (category) =>
						selectedCategory === category.key && (
						  <div key={category.key} className="tab-pane active">
							<div className="d-flex justify-content-between align-items-center mb-3">
							  <h6 className="mb-0">{category.name} Packages</h6>
							  <div className="d-flex align-items-center">
								<select
								  className="form-select form-select-sm me-2"
								  onChange={(e) =>
									addPackageToCategory(
									  category.key,
									  e.target.value,
									)
								  }
								  value=""
								>
								  <option value="">Add Package</option>
								  {packages?.map((pkg) => (
									<option key={pkg._id} value={pkg._id}>
									  {pkg.name}
									</option>
								  ))}
								</select>
							  </div>
							</div>

							{/* Package List */}
							<div className="row">
							  {formData[category.key]?.bundles?.map(
								(bundle, index) => (
								  <div key={index} className="col-md-6 mb-3">
									<div className="card border-start border-4 border-primary">
									  <div className="card-body p-3">
										<div className="d-flex justify-content-between align-items-start">
										  <div className="flex-grow-1">
											<h6 className="card-title mb-1">
											  {getPackageName(
												bundle.productPackage,
											  )}
											</h6>
											<div className="d-flex align-items-center">
											  <label className="form-label small me-2 mb-0">
												Priority:
											  </label>
											  <input
												type="number"
												value={bundle.priority}
												onChange={(e) =>
												  updatePackagePriority(
													category.key,
													index,
													e.target.value,
												  )
												}
												className="form-control form-control-sm"
												style={{ width: "80px" }}
												min="1"
											  />
											</div>
										  </div>
										  <button
											type="button"
											onClick={() =>
											  removePackageFromCategory(
												category.key,
												index,
											  )
											}
											className="btn btn-outline-danger btn-sm ms-2"
										  >
											<Trash size={14} />
										  </button>
										</div>
									  </div>
									</div>
								  </div>
								),
							  )}
							  {(!formData[category.key]?.bundles ||
								formData[category.key].bundles.length ===
								  0) && (
								<div className="col-12">
								  <div className="text-center py-4 text-muted">
									<i className="fas fa-box-open fa-2x mb-2"></i>
									<p>
									  No packages added to {category.name}{" "}
									  category yet.
									</p>
								  </div>
								</div>
							  )}
							</div>
						  </div>
						),
					)}
				  </div>
				</div>
			  </div>

			  {/* Welcome Banner Section */}
			  <div className="card shadow-sm mb-4">
				<div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
				  <h5 className="mb-0 d-flex align-items-center">
					<i className="fas fa-star me-2"></i>
					Welcome Banner
				  </h5>
				  <button
					type="button"
					onClick={() => setShowWelcomeBannerForm(!showWelcomeBannerForm)}
					className="btn btn-dark btn-sm"
				  >
					<Edit size={16} className="me-1" />
					{showWelcomeBannerForm ? 'Hide' : 'Configure'}
				  </button>
				</div>
				<div className="card-body">
				  {showWelcomeBannerForm ? (
					<div className="row">
					  <div className="col-md-6">
						<div className="mb-3">
						  <label className="form-label fw-bold">Asset Upload</label>
						  <input
							type="file"
							className="form-control"
							accept="image/*,video/*"
							onChange={handleWelcomeBannerFileSelect}
							disabled={uploadingWelcomeBanner}
						  />
						  {welcomeBannerFile && (
							<div className="mt-2">
							  <small className="text-success">
								<i className="fas fa-check me-1"></i>
								File selected: {welcomeBannerFile.name}
							  </small>
							  <button
								type="button"
								className="btn btn-primary btn-sm ms-2"
								onClick={uploadWelcomeBannerAsset}
								disabled={uploadingWelcomeBanner}
							  >
								{uploadingWelcomeBanner ? 'Uploading...' : 'Upload'}
							  </button>
							</div>
						  )}
						</div>
						<div className="mb-3">
						  <label className="form-label fw-bold">Asset Path/URL</label>
						  <input
							type="text"
							className="form-control"
							placeholder="Asset path or URL"
							value={formData.welcomeBanner?.asset || ''}
							onChange={(e) => handleWelcomeBannerChange('asset', e.target.value)}
						  />
						</div>
						<div className="mb-3">
						  <label className="form-label fw-bold">Asset Type</label>
						  <select
							className="form-select"
							value={formData.welcomeBanner?.assetType || 'image'}
							onChange={(e) => handleWelcomeBannerChange('assetType', e.target.value)}
						  >
							<option value="image">Image</option>
							<option value="video">Video</option>
						  </select>
						</div>
						<div className="mb-3">
						  <div className="form-check">
							<input
							  className="form-check-input"
							  type="checkbox"
							  checked={formData.welcomeBanner?.active || false}
							  onChange={(e) => handleWelcomeBannerChange('active', e.target.checked)}
							  id="welcomeBannerActive"
							/>
							<label className="form-check-label fw-bold" htmlFor="welcomeBannerActive">
							  Active
							</label>
						  </div>
						</div>
						{formData.welcomeBanner?.asset && (
						  <div className="mb-3">
							<button
							  type="button"
							  className="btn btn-outline-info btn-sm w-100"
							  onClick={() => setPreviewAsset(formData.welcomeBanner)}
							>
							  <Eye size={16} className="me-2" />
							  Preview Asset
							</button>
						  </div>
						)}
					  </div>
					  <div className="col-md-6">
						<div className="mb-3">
						  <label className="form-label fw-bold">Close Icon Color</label>
						  <input
							type="color"
							className="form-control form-control-color"
							value={formData.welcomeBanner?.closeIcon?.color || '#000000'}
							onChange={(e) => handleWelcomeBannerChange('closeIcon.color', e.target.value)}
						  />
						</div>
						<div className="mb-3">
						  <label className="form-label fw-bold">Button Text</label>
						  <input
							type="text"
							className="form-control"
							placeholder="Button text"
							value={formData.welcomeBanner?.button?.buttonText || ''}
							onChange={(e) => handleWelcomeBannerChange('button.buttonText', e.target.value)}
						  />
						</div>
						<div className="mb-3">
						  <label className="form-label fw-bold">Button Text Color</label>
						  <input
							type="color"
							className="form-control form-control-color"
							value={formData.welcomeBanner?.button?.textColor || '#ffffff'}
							onChange={(e) => handleWelcomeBannerChange('button.textColor', e.target.value)}
						  />
						</div>
						<div className="mb-3">
						  <label className="form-label fw-bold">Button Background Color</label>
						  <input
							type="color"
							className="form-control form-control-color"
							value={formData.welcomeBanner?.button?.backgroundColor || '#007bff'}
							onChange={(e) => handleWelcomeBannerChange('button.backgroundColor', e.target.value)}
						  />
						</div>
					  </div>
					</div>
				  ) : (
					<div className="text-center py-3">
					  {formData.welcomeBanner?.asset ? (
						<div className="row align-items-center">
						  <div className="col-md-8">
							<div className="mb-2">
							  <strong>Asset:</strong> 
							  <span className="text-truncate d-block small text-muted">
								{formData.welcomeBanner.asset}
							  </span>
							</div>
							<div className="mb-2">
							  <span className={`badge ${formData.welcomeBanner?.active ? 'bg-success' : 'bg-secondary'}`}>
								{formData.welcomeBanner?.active ? 'Active' : 'Inactive'}
							  </span>
							  <span className="badge bg-info ms-2">
								{formData.welcomeBanner?.assetType || 'image'}
							  </span>
							</div>
							<div className="mt-3">
							  <button
								type="button"
								className="btn btn-outline-primary btn-sm"
								onClick={() => setPreviewAsset(formData.welcomeBanner)}
							  >
								<Eye size={16} className="me-2" />
								Preview Welcome Banner
							  </button>
							</div>
						  </div>
						  <div className="col-md-4">
							{formData.welcomeBanner?.assetType === 'image' && (
							  <div className="text-center">
								<img
								  src={
									formData.welcomeBanner.asset.startsWith("http")
									  ? formData.welcomeBanner.asset
									  : `${profile.assetPath || ""}/${formData.welcomeBanner.asset}`
								  }
								  alt="Welcome Banner Preview"
								  style={{
									maxWidth: "100%",
									maxHeight: "80px",
									objectFit: "cover",
									borderRadius: "4px",
									border: "1px solid #dee2e6"
								  }}
								  onError={(e) => {
									e.target.style.display = "none";
								  }}
								/>
							  </div>
							)}
							{formData.welcomeBanner?.assetType === 'video' && (
							  <div className="text-center">
								<div className="bg-light rounded d-flex align-items-center justify-content-center" 
									 style={{ height: "80px", border: "1px solid #dee2e6" }}>
								  <Film size={32} className="text-muted" />
								</div>
							  </div>
							)}
						  </div>
						</div>
					  ) : (
						<p className="text-muted">No welcome banner configured</p>
					  )}
					</div>
				  )}
				</div>
			  </div>

			  {/* Banners Section */}
			  <div className="card shadow-sm mb-4">
				<div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
				  <h5 className="mb-0 d-flex align-items-center">
					<i className="fas fa-images me-2"></i>
					Banners ({formData.banner?.length || 0})
				  </h5>
				  <button
					type="button"
					onClick={() => openBannerForm()}
					className="btn btn-light btn-sm"
				  >
					<Plus size={16} className="me-1" />
					Add Banner
				  </button>
				</div>
				<div className="card-body">
				  {formData.banner && formData.banner.length > 0 ? (
					<div className="row">
					  {formData.banner.map((banner, index) => (
						<div key={index} className="col-md-6 mb-3">
						  <div className="card border">
							<div className="card-body">
							  <div className="d-flex justify-content-between align-items-center mb-2">
								<h6 className="card-title mb-0">
								  Banner #{index + 1}
								</h6>
								<div>
								  <button
									type="button"
									className="btn btn-outline-primary btn-sm me-2"
									onClick={() => setPreviewAsset(banner)}
								  >
									<Eye size={14} />
								  </button>
								  <button
									type="button"
									onClick={() => removeBannerItem(index)}
									className="btn btn-outline-danger btn-sm"
								  >
									<Trash size={14} />
								  </button>
								</div>
							  </div>
							  <div className="small text-muted">
								<div>Priority: {banner.priority}</div>
								<div>Type: {banner.assetType}</div>
								<div className="text-truncate">Asset: {banner.asset}</div>
							  </div>
							</div>
						  </div>
						</div>
					  ))}
					</div>
				  ) : (
					<div className="text-center py-4 text-muted">
					  <i className="fas fa-images fa-3x mb-3"></i>
					  <p>
						No banners added yet. Click "Add Banner" to create one.
					  </p>
					</div>
				  )}
				</div>
			  </div>

			  {/* Banner Form Modal */}
			  {showBannerForm && (
				<div
				  className="modal show d-block"
				  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
				>
				  <div className="modal-dialog">
					<div className="modal-content">
					  <div className="modal-header">
						<h5 className="modal-title">Add Banner Item</h5>
						<button
						  type="button"
						  className="btn-close"
						  onClick={() => {
							setShowBannerForm(false);
							setNewBannerItem(bannerItemInitialState);
							setSelectedFile(null);
						  }}
						></button>
					  </div>
					  <div className="modal-body">
						<div className="mb-3">
						  <label className="form-label fw-bold">Asset Type</label>
						  <select
							className="form-select"
							value={newBannerItem.assetType}
							onChange={(e) =>
							  setNewBannerItem({
								...newBannerItem,
								assetType: e.target.value,
							  })
							}
						  >
							<option value="image">Image</option>
							<option value="video">Video</option>
						  </select>
						</div>

						<div className="mb-3">
						  <label className="form-label fw-bold">Upload File</label>
						  <input
							type="file"
							className="form-control"
							accept={
							  newBannerItem.assetType === "video"
								? "video/*"
								: "image/*"
							}
							onChange={handleFileSelect}
							disabled={uploadingFile}
						  />
						  {selectedFile && (
							<div className="mt-2 text-success">
							  <i className="fas fa-check me-1"></i>
							  File selected: {selectedFile.name}
							</div>
						  )}
						</div>

						<div className="text-center text-muted mb-3">OR</div>

						<div className="mb-3">
						  <label className="form-label fw-bold">Asset Path/URL</label>
						  <input
							type="text"
							className="form-control"
							placeholder="Enter asset path or URL"
							value={newBannerItem.asset}
							onChange={(e) =>
							  setNewBannerItem({
								...newBannerItem,
								asset: e.target.value,
							  })
							}
						  />
						</div>

						<div className="mb-3">
						  <label className="form-label fw-bold">Priority</label>
						  <input
							type="number"
							className="form-control"
							placeholder="Priority order"
							value={newBannerItem.priority}
							onChange={(e) =>
							  setNewBannerItem({
								...newBannerItem,
								priority: parseInt(e.target.value) || 1,
							  })
							}
							min="1"
						  />
						</div>
					  </div>
					  <div className="modal-footer">
						<button
						  type="button"
						  className="btn btn-secondary"
						  onClick={() => {
							setShowBannerForm(false);
							setNewBannerItem(bannerItemInitialState);
							setSelectedFile(null);
						  }}
						>
						  Cancel
						</button>
						<button
						  type="button"
						  className="btn btn-primary"
						  onClick={addBannerItem}
						  disabled={uploadingFile || !newBannerItem.asset.trim()}
						>
						  {uploadingFile ? (
							<>
							  <i className="fas fa-spinner fa-spin me-1"></i>
							  Uploading...
							</>
						  ) : (
							<>
							  <Plus size={16} className="me-1" />
							  Add Banner
							</>
						  )}
						</button>
					  </div>
					</div>
				  </div>
				</div>
			  )}


			  {/* Asset Preview Modal */}
			  {previewAsset && (
				<div
				  className="modal show d-block"
				  style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1060 }}
				>
				  <div className="modal-dialog modal-lg modal-dialog-centered">
					<div className="modal-content">
					  <div className="modal-header">
						<h5 className="modal-title d-flex align-items-center">
						  {previewAsset.assetType === "video" ? (
							<Film size={20} className="me-2" />
						  ) : (
							<Image size={20} className="me-2" />
						  )}
						  {previewAsset.assetType.charAt(0).toUpperCase() +
							previewAsset.assetType.slice(1)}{" "}
						  Preview
						</h5>
						<button
						  type="button"
						  className="btn-close"
						  onClick={() => setPreviewAsset(null)}
						></button>
					  </div>
					  <div className="modal-body text-center">
						<div className="mb-3">
						  <strong>Asset Path:</strong>
						  <div
							className="text-muted small mt-1"
							style={{ wordBreak: "break-all" }}
						  >
							{previewAsset.asset}
						  </div>
						  <div className="mt-2">
							<span className="badge bg-info">
							  Priority: {previewAsset.priority}
							</span>
							<span className="badge bg-secondary ms-2">
							  {previewAsset.assetType}
							</span>
						  </div>
						</div>

						<div
						  className="border rounded p-3"
						  style={{
							minHeight: "300px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						  }}
						>
						  {previewAsset.assetType === "image" ? (
							<div>
							  <img
								src={
								  previewAsset.asset.startsWith("http")
									? previewAsset.asset
									: `${profile.assetPath || ""}/${previewAsset.asset}`
								}
								alt="Preview"
								style={{
								  maxWidth: "100%",
								  maxHeight: "400px",
								  objectFit: "contain",
								}}
								onError={(e) => {
								  e.target.style.display = "none";
								  e.target.nextSibling.style.display = "block";
								}}
							  />
							  <div
								style={{ display: "none" }}
								className="text-center"
							  >
								<Image size={48} className="text-muted mb-3" />
								<p className="text-muted">
								  Image could not be loaded
								  <br />
								  <small>Check if the path is correct</small>
								</p>
							  </div>
							</div>
						  ) : previewAsset.assetType === "video" ? (
							<div>
							  <video
								controls
								style={{ maxWidth: "100%", maxHeight: "400px" }}
								onError={(e) => {
								  e.target.style.display = "none";
								  e.target.nextSibling.style.display = "block";
								}}
							  >
								<source
								  src={
									previewAsset.asset.startsWith("http")
									  ? previewAsset.asset
									  : `${profile.assetPath || ""}/${previewAsset.asset}`
								  }
								/>
								Your browser does not support the video tag.
							  </video>
							  <div
								style={{ display: "none" }}
								className="text-center"
							  >
								<Film size={48} className="text-muted mb-3" />
								<p className="text-muted">
								  Video could not be loaded
								  <br />
								  <small>Check if the path is correct</small>
								</p>
							  </div>
							</div>
						  ) : (
							<div className="text-center">
							  <i className="fas fa-file fa-3x text-muted mb-3"></i>
							  <p className="text-muted">
								Preview not available for this asset type
							  </p>
							</div>
						  )}
						</div>
					  </div>
					  <div className="modal-footer">
						<button
						  type="button"
						  className="btn btn-secondary"
						  onClick={() => setPreviewAsset(null)}
						>
						  Close
						</button>
						{previewAsset.asset.startsWith("http") && (
						  <a
							href={previewAsset.asset}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-primary"
						  >
							<i className="fas fa-external-link-alt me-1"></i>
							Open in New Tab
						  </a>
						)}
					  </div>
					</div>
				  </div>
				</div>
			  )}
			</div>

			{/* Sidebar */}
			<div className="col-lg-4">
			  {/* Summary Card */}
			  <div className="card shadow-sm mb-4">
				<div className="card-header bg-secondary text-white">
				  <h5 className="mb-0 d-flex align-items-center">
					<i className="fas fa-chart-pie me-2"></i>
					Profile Summary
				  </h5>
				</div>
				<div className="card-body">
				  <div className="row g-3">
					<div className="col-6">
					  <div className="text-center">
						<div className="h4 text-primary mb-0">
						  {Object.values(formData).reduce((total, category) => {
							return total + (category?.bundles?.length || 0);
						  }, 0)}
						</div>
						<div className="small text-muted">Total Packages</div>
					  </div>
					</div>
					<div className="col-6">
					  <div className="text-center">
						<div className="h4 text-info mb-0">
						  {formData.banner?.length || 0}
						</div>
						<div className="small text-muted">Banners</div>
					  </div>
					</div>
					<div className="col-6">
					  <div className="text-center">
						<div className="h4 text-success mb-0">
						  {formData.cansLimit}
						</div>
						<div className="small text-muted">Cans Limit</div>
					  </div>
					</div>
					<div className="col-6">
					  <div className="text-center">
						<div className="h4 text-warning mb-0">
						  {formData.profileType?.toUpperCase()}
						</div>
						<div className="small text-muted">Profile Type</div>
					  </div>
					</div>
					<div className="col-12">
					  <div className="text-center pt-2 border-top">
						<div className={`h6 ${formData.welcomeBanner?.active ? 'text-success' : 'text-muted'} mb-0`}>
						  <i className="fas fa-star me-2"></i>
						  Welcome Banner {formData.welcomeBanner?.active ? 'Active' : 'Inactive'}
						</div>
						<div className="small text-muted">
						  {formData.welcomeBanner?.asset ? 'Configured' : 'Not configured'}
						</div>
					  </div>
					</div>
				  </div>
				  {formData.membership && (
					<div className="mt-3 pt-3 border-top">
					  <div className="text-center">
						<div className="small text-muted mb-1">Membership</div>
						<div className="fw-bold text-primary">
						  {getMembershipName(formData.membership)}
						</div>
					  </div>
					</div>
				  )}
				</div>
			  </div>

			  {/* Actions Card */}
			  <div className="card shadow-sm">
				<div className="card-body text-center">
				  <button
					type="submit"
					className="btn btn-primary btn-lg w-100"
				  >
					<Save size={16} className="me-2" />
					{isNewProfile ? "Create Profile" : "Update Profile"}
				  </button>
				</div>
			  </div>
			</div>
		  </div>
		</form>
	  </div>
	</Fragment>
  );
};

Profile.propTypes = {
  createProfile: PropTypes.func.isRequired,
  getProfile: PropTypes.func.isRequired,
  updateProfile: PropTypes.func.isRequired,
  getPackagesMemberships: PropTypes.func.isRequired,
  clearProfile: PropTypes.func.isRequired,
  clearNewProfileCreated: PropTypes.func.isRequired,
  uploadProfileBannerAsset: PropTypes.func.isRequired,
  deleteProfileBannerAsset: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  profile: PropTypes.object,
  packages: PropTypes.array,
  memberships: PropTypes.array,
  newProfileCreated: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  loading: state.profile.loading,
  profile: state.profile.profile,
  packages: state.profile.packages,
  memberships: state.profile.memberships,
  newProfileCreated: state.profile.newProfileCreated,
});

export default connect(mapStateToProps, {
  createProfile,
  getProfile,
  updateProfile,
  getPackagesMemberships,
  clearProfile,
  clearNewProfileCreated,
  uploadProfileBannerAsset,
  deleteProfileBannerAsset,
})(Profile);
